import type { Player, TeamId, WeaponDef } from '@use/shared';
import { TANK_WEAPONS, INFANTRY_WEAPONS, ALL_WEAPONS, TANK_HP, INFANTRY_HP } from '@use/shared';

export class UIManager {
  // ─── Element References ───
  private redScore = document.getElementById('red-score')!;
  private blueScore = document.getElementById('blue-score')!;
  private roundTimer = document.getElementById('round-timer')!;
  private healthBar = document.getElementById('health-bar')!;
  private healthText = document.getElementById('health-text')!;
  private weaponBar = document.getElementById('weapon-bar')!;
  private crosshair = document.getElementById('crosshair')!;
  private shotPower = document.getElementById('shot-power')!;
  private shotPowerFill = document.getElementById('shot-power-fill')!;
  private killFeed = document.getElementById('kill-feed')!;
  private rosterLeft = document.getElementById('team-roster-left')!;
  private rosterRight = document.getElementById('team-roster-right')!;
  private notifications = document.getElementById('notifications')!;
  private respawnOverlay = document.getElementById('respawn-overlay')!;
  private respawnTimerText = document.getElementById('respawn-timer-text')!;
  private scoreboard = document.getElementById('scoreboard')!;
  private scoreboardTable = document.getElementById('scoreboard-table')!;
  private loadoutScreen = document.getElementById('loadout-screen')!;
  private loadoutGrid = document.getElementById('loadout-grid')!;
  private loadoutConfirm = document.getElementById('loadout-confirm')!;
  private loadoutTimer = document.getElementById('loadout-timer')!;

  private selectedLoadout: Set<string> = new Set();
  public onLoadoutConfirmed?: (weaponIds: string[]) => void;

  constructor() {
    this.loadoutConfirm.addEventListener('click', () => {
      if (this.selectedLoadout.size === 5 && this.onLoadoutConfirmed) {
        this.onLoadoutConfirmed(Array.from(this.selectedLoadout));
        this.hideLoadoutScreen();
      }
    });
  }

  // ─── Scores ───

  public updateTeamScores(redScore: number, blueScore: number): void {
    this.redScore.textContent = String(redScore);
    this.blueScore.textContent = String(blueScore);
  }

  // ─── Timer ───

  public updateRoundTimer(secondsRemaining: number): void {
    const min = Math.floor(secondsRemaining / 60);
    const sec = Math.floor(secondsRemaining % 60);
    this.roundTimer.textContent = `${min}:${sec.toString().padStart(2, '0')}`;
  }

  // ─── Health ───

  public updateHealth(current: number, max: number, mode: 'tank' | 'infantry'): void {
    const pct = Math.max(0, Math.min(100, (current / max) * 100));
    this.healthBar.style.width = `${pct}%`;
    this.healthText.textContent = `${Math.ceil(current)} / ${max}`;

    // Color based on percentage
    if (pct > 60) {
      this.healthBar.style.background = 'linear-gradient(to right, #44ff44, #88ff44)';
    } else if (pct > 30) {
      this.healthBar.style.background = 'linear-gradient(to right, #ffaa00, #ffcc00)';
    } else {
      this.healthBar.style.background = 'linear-gradient(to right, #ff2222, #ff4444)';
    }
  }

  // ─── Weapon Bar ───

  public buildWeaponBar(weapons: WeaponDef[], activeIndex: number, cooldowns: Record<string, number>, ammo?: Record<string, number>): void {
    this.weaponBar.innerHTML = '';
    weapons.forEach((w, i) => {
      const slot = document.createElement('div');
      const ammoCount = ammo?.[w.id] ?? -1;
      const depleted = ammoCount === 0;
      slot.className = 'weapon-slot' + (i === activeIndex ? ' active' : '') + (depleted ? ' depleted' : '');
      const ammoText = ammoCount === -1 ? '∞' : String(ammoCount);
      slot.innerHTML = `
        <span class="key">${i + 1}</span>
        <span>${w.name.split(' ').map(s => s[0]).join('')}</span>
        <span class="ammo-count">${ammoText}</span>
        <div class="cooldown-overlay" style="height: ${this.getCooldownPct(w.id, cooldowns)}%"></div>
      `;
      if (depleted) {
        slot.style.opacity = '0.35';
      }
      slot.addEventListener('click', () => {
        // weapon switch handled by input manager
      });
      this.weaponBar.appendChild(slot);
    });
  }

  public updateWeaponCooldowns(weapons: WeaponDef[], cooldowns: Record<string, number>): void {
    const slots = this.weaponBar.querySelectorAll('.weapon-slot');
    slots.forEach((slot, i) => {
      const overlay = slot.querySelector('.cooldown-overlay') as HTMLElement;
      if (overlay && weapons[i]) {
        overlay.style.height = `${this.getCooldownPct(weapons[i].id, cooldowns)}%`;
      }
    });
  }

  private getCooldownPct(weaponId: string, cooldowns: Record<string, number>): number {
    const remaining = cooldowns[weaponId] || 0;
    const def = ALL_WEAPONS.get(weaponId);
    if (!def || remaining <= 0) return 0;
    return Math.min(100, (remaining / def.cooldownMs) * 100);
  }

  // ─── Crosshair ───

  public showCrosshair(show: boolean): void {
    this.crosshair.style.display = show ? 'block' : 'none';
  }

  public updateShotPower(normalizedPower: number, show: boolean): void {
    if (!show) {
      this.shotPower.style.display = 'none';
      this.shotPowerFill.style.width = '0%';
      return;
    }

    const clamped = Math.max(0, Math.min(1, normalizedPower));
    this.shotPower.style.display = 'block';
    this.shotPowerFill.style.width = `${clamped * 100}%`;
  }

  // ─── Kill Feed ───

  public addKillEntry(killerName: string, victimName: string, weaponName: string, killerTeam: TeamId): void {
    const entry = document.createElement('div');
    entry.className = 'kill-entry';
    const killerColor = killerTeam === 'red' ? '#ff4444' : '#4488ff';
    const victimColor = killerTeam === 'red' ? '#4488ff' : '#ff4444';
    entry.innerHTML = `<span style="color:${killerColor}">${killerName}</span> [${weaponName}] <span style="color:${victimColor}">${victimName}</span>`;
    this.killFeed.appendChild(entry);

    // Fade out after 5s
    setTimeout(() => {
      entry.style.opacity = '0';
      setTimeout(() => entry.remove(), 500);
    }, 5000);
  }

  // ─── Team Roster ───

  public updateRosters(redPlayers: Player[], bluePlayers: Player[]): void {
    this.rosterLeft.innerHTML = this.buildRosterHTML(redPlayers, 'red');
    this.rosterRight.innerHTML = this.buildRosterHTML(bluePlayers, 'blue');
  }

  private buildRosterHTML(players: Player[], team: TeamId): string {
    const color = team === 'red' ? '#ff4444' : '#4488ff';
    return players.map(p => {
      const maxHP = p.mode === 'tank' ? TANK_HP : INFANTRY_HP;
      const currentHP = p.mode === 'tank' ? p.tankHP : p.infantryHP;
      const pct = Math.max(0, (currentHP / maxHP) * 100);
      const icon = p.mode === 'tank' ? '🔲' : '🏃';
      const alive = p.alive ? '' : ' style="opacity:0.4"';
      return `<div class="roster-entry"${alive}>
        <span>${icon}</span>
        <span style="color:${color};min-width:60px">${p.name}</span>
        <div class="roster-hp">
          <div class="roster-hp-fill" style="width:${pct}%;background:${color}"></div>
        </div>
      </div>`;
    }).join('');
  }

  // ─── Notifications ───

  public showNotification(text: string, duration: number = 3000, color: string = '#fff'): void {
    this.notifications.textContent = text;
    this.notifications.style.color = color;
    this.notifications.style.opacity = '1';
    setTimeout(() => {
      this.notifications.style.opacity = '0';
    }, duration);
  }

  // ─── Respawn ───

  public showRespawnOverlay(secondsLeft: number): void {
    this.respawnOverlay.style.display = 'flex';
    this.respawnTimerText.textContent = `Respawning in ${Math.ceil(secondsLeft)}...`;
  }

  public hideRespawnOverlay(): void {
    this.respawnOverlay.style.display = 'none';
  }

  // ─── Scoreboard ───

  public showScoreboard(show: boolean): void {
    this.scoreboard.style.display = show ? 'block' : 'none';
  }

  public updateScoreboard(redPlayers: Player[], bluePlayers: Player[]): void {
    let html = `<tr><th>Player</th><th>K</th><th>D</th><th>Dmg</th><th>HP</th></tr>`;
    html += `<tr><td class="sb-team-header" style="color:#ff4444" colspan="5">RED TEAM</td></tr>`;
    for (const p of redPlayers) {
      const hp = p.mode === 'tank' ? p.tankHP : p.infantryHP;
      html += `<tr><td style="color:#ff4444">${p.name}</td><td>${p.kills}</td><td>${p.deaths}</td><td>${p.damageDealt}</td><td>${Math.ceil(hp)}</td></tr>`;
    }
    html += `<tr><td class="sb-team-header" style="color:#4488ff" colspan="5">BLUE TEAM</td></tr>`;
    for (const p of bluePlayers) {
      const hp = p.mode === 'tank' ? p.tankHP : p.infantryHP;
      html += `<tr><td style="color:#4488ff">${p.name}</td><td>${p.kills}</td><td>${p.deaths}</td><td>${p.damageDealt}</td><td>${Math.ceil(hp)}</td></tr>`;
    }
    this.scoreboardTable.innerHTML = html;
  }

  // ─── Loadout Screen ───

  public showLoadoutScreen(): void {
    this.loadoutScreen.style.display = 'flex';
    this.selectedLoadout.clear();

    this.loadoutGrid.innerHTML = '';
    for (const w of TANK_WEAPONS) {
      const card = document.createElement('div');
      card.className = 'loadout-weapon';
      card.innerHTML = `<div class="wname">${w.name}</div><div class="wdesc">${w.description}</div>`;
      card.addEventListener('click', () => {
        if (this.selectedLoadout.has(w.id)) {
          this.selectedLoadout.delete(w.id);
          card.classList.remove('selected');
        } else if (this.selectedLoadout.size < 5) {
          this.selectedLoadout.add(w.id);
          card.classList.add('selected');
        }
      });
      this.loadoutGrid.appendChild(card);
    }
  }

  public updateLoadoutTimer(seconds: number): void {
    this.loadoutTimer.textContent = `${Math.ceil(seconds)}s`;
  }

  public hideLoadoutScreen(): void {
    this.loadoutScreen.style.display = 'none';
  }

  public getSelectedLoadout(): string[] {
    return Array.from(this.selectedLoadout);
  }

  // ─── Mode Switch ───

  public setMode(mode: 'tank' | 'infantry'): void {
    this.showCrosshair(mode === 'infantry');
  }
}
